
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../App.jsx';
import { ArrowLeft, Send, CheckCircle, PenSquare, Plus, Camera, Video, Image as ImageIcon, MapPin, Flag, Star, RefreshCw, CalendarCheck, Zap, XCircle, Shield } from 'lucide-react';
import AuthLoadingScreen from './AuthLoadingScreen.jsx';
import { doc, onSnapshot, collection, query, orderBy, limit, startAfter, getDocs, where } from 'firebase/firestore';
import { CHATS_COLLECTION, PROPOSALS_COLLECTION } from '../config/constants';
import FinalizeServiceModal from '../components/chat/FinalizeServiceModal.jsx';
import QuoteModal from '../components/common/QuoteModal.jsx';
import QuoteMessage from '../components/chat/QuoteMessage.jsx';
import LocationTypeModal from '../components/chat/LocationTypeModal.jsx';
import AddressInputModal from '../components/chat/AddressInputModal.jsx';
import InvoiceMessage from '../components/chat/InvoiceMessage.jsx';
import ReportModal from '../components/common/ReportModal.jsx';
import ConfirmationModal from '../components/common/ConfirmationModal.jsx';

const MESSAGES_PER_PAGE = 30;

const ChatScreen = () => {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const { user, db, activeRole, sendMessage, sendInvoiceMessage, sendQuoteMessage, updateQuoteMessage, sendImageMessage, respondToQuote, fullUserData, showMessage, getOrCreateChat, cancelService } = useAppContext();

    const [messages, setMessages] = useState([]);
    const [chatInfo, setChatInfo] = useState(null);
    const [leftoverProposals, setLeftoverProposals] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [quoteToEdit, setQuoteToEdit] = useState(null);
    const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isQuickReplyOpen, setIsQuickReplyOpen] = useState(false);
    const [isEndingChat, setIsEndingChat] = useState(false);
    const [showEndConfirmation, setShowEndConfirmation] = useState(false);

    const [firstMessage, setFirstMessage] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatContainerRef = useRef(null);
    const initialLoadRef = useRef(true);
    const textAreaRef = useRef(null);

    // Identificação de suporte
    const isSupportChat = useMemo(() => chatInfo?.participants?.includes("SYSTEM_ADMIN_SUPPORT"), [chatInfo]);

    useEffect(() => {
        if (!chatId || !db) { setIsLoading(false); return; }

        const chatDocRef = doc(db, CHATS_COLLECTION, chatId);
        const unsubInfo = onSnapshot(chatDocRef, (doc) => {
            if (doc.exists()) { setChatInfo(doc.data()); }
            setIsLoading(false);
        });

        const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(MESSAGES_PER_PAGE));

        const unsubMessages = onSnapshot(q, (snapshot) => {
            const newMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).reverse();
            setMessages(newMessages);
            setFirstMessage(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(snapshot.docs.length === MESSAGES_PER_PAGE);
        });

        return () => { unsubInfo(); unsubMessages(); };
    }, [chatId, db]);

    useEffect(() => {
        const checkLeftoverProposals = async () => {
            // CORREÇÃO CIRÚRGICA: Ignora busca de propostas em chats de suporte para evitar erro/piscada
            if (isSupportChat) return;

            if (chatInfo?.status === 'Finalizado' || chatInfo?.status === 'Pago') {
                try {
                    const q = query(
                        collection(db, PROPOSALS_COLLECTION),
                        where('chatId', '==', chatId),
                        where('status', '==', 'accepted')
                    );
                    const snap = await getDocs(q);
                    setLeftoverProposals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                } catch (e) { console.error("Erro ao buscar propostas pendentes:", e); }
            }
        };
        checkLeftoverProposals();
    }, [chatInfo?.status, chatId, db, isSupportChat]);

    useEffect(() => {
        if (initialLoadRef.current && messages.length > 0) {
            messagesEndRef.current?.scrollIntoView();
            initialLoadRef.current = false;
        }
    }, [messages]);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';
            textAreaRef.current.style.height = `${Math.min(textAreaRef.current.scrollHeight, 120)}px`;
        }
    }, [newMessage]);

    const loadPreviousMessages = useCallback(async () => {
        if (isLoadingMore || !hasMore || !firstMessage) return;
        setIsLoadingMore(true);
        const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), startAfter(firstMessage), limit(MESSAGES_PER_PAGE));
        const querySnapshot = await getDocs(q);
        const oldMessages = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).reverse();
        if (oldMessages.length > 0) {
            const prevScrollHeight = chatContainerRef.current.scrollHeight;
            setMessages(prev => [...oldMessages, ...prev]);
            setFirstMessage(querySnapshot.docs[querySnapshot.docs.length - 1]);
            requestAnimationFrame(() => {
                const newScrollHeight = chatContainerRef.current.scrollHeight;
                chatContainerRef.current.scrollTop += newScrollHeight - prevScrollHeight;
            });
        }
        setHasMore(querySnapshot.docs.length === MESSAGES_PER_PAGE);
        setIsLoadingMore(false);
    }, [isLoadingMore, hasMore, firstMessage, chatId, db]);

    const handleScroll = (e) => { if (e.currentTarget.scrollTop === 0) loadPreviousMessages(); };

    const handleSendMessage = (textToSend = null) => {
        const text = textToSend || newMessage;
        if (text.trim() !== '' && user) {
            const senderId = (isSupportChat && fullUserData?.roles?.admin) ? "SYSTEM_ADMIN_SUPPORT" : user.uid;
            sendMessage(chatId, { text: text.trim(), senderId, type: 'text' });
            if (!textToSend) setNewMessage('');
            if (textAreaRef.current) textAreaRef.current.style.height = 'auto';
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            setIsQuickReplyOpen(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleOpenQuoteModal = (message = null) => { setQuoteToEdit(message); setIsQuoteModalOpen(true); };
    const handleCloseQuoteModal = () => { setQuoteToEdit(null); setIsQuoteModalOpen(false); };
    const handleSubmitQuote = (quoteData) => { if (quoteToEdit) { updateQuoteMessage(quoteToEdit.proposalId, quoteData, chatId); } else { sendQuoteMessage(chatId, { ...quoteData, serviId: user.uid }); } handleCloseQuoteModal(); };
    const handleRespondToQuote = (proposalId, response) => { if(!proposalId) return showMessage("ID da proposta não encontrado.", "error"); respondToQuote(proposalId, response); };
    const handleFinalizeSubmit = (invoiceData) => { if (chatId && invoiceData) { sendInvoiceMessage(chatId, { ...invoiceData, serviId: user.uid }); } setIsFinalizeModalOpen(false); };
    const handleNavigateToCapture = (mode) => { navigate('/capture', { state: { mode: mode, chatId: chatId } }); setIsAttachmentMenuOpen(false); };
    const handleGalleryClick = () => { fileInputRef.current.click(); setIsAttachmentMenuOpen(false); };
    const handleFileSelect = (event) => { const file = event.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = (e) => sendImageMessage(chatId, { name: file.name, dataUrl: e.target.result }); reader.readAsDataURL(file); event.target.value = null; };

    const handleSendLocation = async (coords) => {
        if (!coords) return;
        let addressText = `Localização: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`;
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}&addressdetails=1`);
            const data = await response.json();
            if (data && data.address) {
                const { road, city, town, village, state } = data.address;
                addressText = `${road || 'Rua não encontrada'}, ${city || town || village}, ${state}`;
            } else if (data && data.display_name) {
                addressText = data.display_name;
            }
        } catch (e) { console.error("Erro ao converter coordenadas:", e); }

        sendMessage(chatId, {
            senderId: user.uid,
            type: 'location',
            text: addressText,
            location: { latitude: coords.latitude, longitude: coords.longitude }
        });
        setIsAddressModalOpen(false);
        setIsLocationModalOpen(false);
    };

    const handleGetCurrentLocation = () => {
        setIsLocationModalOpen(false);
        if (!navigator.geolocation) { return showMessage("Geolocalização não é suportada.", "error"); }
        navigator.geolocation.getCurrentPosition(
            (position) => handleSendLocation(position.coords),
            () => { showMessage("Não foi possível obter sua localização.", "error"); }
        );
    };

    const handleOpenManualLocation = () => {
        setIsLocationModalOpen(false);
        setIsAddressModalOpen(true);
    };

    const handleResumeService = async (proposal) => {
        if (!getOrCreateChat) return;
        const newTitle = `Retomada: ${proposal.items?.[0]?.description || 'Serviço Adiado'}`;
        const newChatId = await getOrCreateChat(chatInfo.serviId, newTitle);
        if (newChatId) {
            showMessage("Novo chamado aberto com sucesso para dar continuidade!", "success");
            navigate(activeRole === 'servi' ? `/servi/chat/${newChatId}` : `/chat/${newChatId}`);
        }
    };

    const handleTryEndChat = async () => {
        if (isEndingChat) return;
        setIsEndingChat(true);

        try {
            const q = query(
                collection(db, PROPOSALS_COLLECTION),
                where('chatId', '==', chatId),
                where('status', '==', 'accepted')
            );
            const snap = await getDocs(q);

            if (!snap.empty) {
                showMessage("Não é possível encerrar pois existe um orçamento aceito. Utilize o botão 'Finalizar' para concluir.", "warning");
                setIsEndingChat(false);
                return;
            }
            setShowEndConfirmation(true);
        } catch (error) {
            console.error("Erro ao verificar propostas:", error);
            showMessage("Erro ao processar.", "error");
        } finally {
            setIsEndingChat(false);
        }
    };

    const handleConfirmEndChat = async () => {
        setShowEndConfirmation(false);
        const reason = activeRole === 'servi'
            ? "O prestador encerrou o atendimento (Sem acordo)."
            : "O cliente cancelou a solicitação.";
        await cancelService(chatId, reason);
    };

    if (isLoading && initialLoadRef.current) return <AuthLoadingScreen message="Carregando conversa..." />;
    if (!chatInfo || !user) return <AuthLoadingScreen message="Carregando dados..." />;

    const otherParticipantId = chatInfo.participants?.find(p => p !== user.uid);
    const otherParticipant = otherParticipantId ? chatInfo.participantDetails?.[otherParticipantId] : null;

    const isServi = activeRole === 'servi';
    const isChatClosed = ['Finalizado', 'Cancelado', 'Pago', 'Concluído', 'Recusado'].includes(chatInfo.status);
    const canFinalize = isServi && !isChatClosed && !isSupportChat;
    const canSendAttachments = !isChatClosed;

    const quickReplies = fullUserData?.profile_servi?.quickReplies || [];

    return (
        <div className="h-full flex flex-col bg-gray-100">
            <header className={`flex items-center justify-between p-4 shadow-sm z-10 shrink-0 transition-colors ${isSupportChat ? 'bg-slate-900 text-white' : 'bg-white text-gray-800'}`}>
                <div className="flex items-center min-w-0 mr-2">
                    <button onClick={() => navigate(-1)} className={`p-2 rounded-full mr-1 shrink-0 transition-colors ${isSupportChat ? 'hover:bg-slate-800' : 'hover:bg-gray-200'}`}>
                        <ArrowLeft className={`w-6 h-6 ${isSupportChat ? 'text-blue-400' : 'text-gray-700'}`} />
                    </button>
                    <div className={`w-10 h-10 rounded-xl overflow-hidden shadow-sm flex items-center justify-center p-0.5 mr-3 flex-shrink-0 border ${isSupportChat ? 'bg-blue-600 border-blue-400/30' : 'bg-white border-gray-100'}`}>
                        {isSupportChat ? (
                            <Shield className="text-white w-6 h-6" />
                        ) : (
                            <img src={otherParticipant?.photoUrl || `https://ui-avatars.com/api/?name=${otherParticipant?.name?.replace(/ /g, '+')}`} alt={otherParticipant?.name} className="w-full h-full object-contain rounded-lg" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <h1 className={`font-bold text-sm truncate leading-tight ${isSupportChat ? 'text-white' : 'text-gray-800'}`}>
                            {isSupportChat ? 'Suporte T-SERVI' : (otherParticipant?.name || 'Usuário')}
                        </h1>
                        {isSupportChat ? (
                            <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest animate-pulse">Atendimento Oficial</span>
                        ) : (
                            isServi && chatInfo.clientRating && (
                                <div className="flex items-center mt-0.5">
                                    <Star size={10} className="text-yellow-400 fill-current mr-1" />
                                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                                        {chatInfo.clientRating.toFixed(1)} • {chatInfo.clientTopTag || 'Bom Cliente'}
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                </div>
                <div className="flex items-center space-x-2 shrink-0">
                    {!isChatClosed && !isSupportChat && (
                        <button onClick={handleTryEndChat} disabled={isEndingChat} title="Encerrar Atendimento" className="p-2 text-gray-400 hover:text-orange-600 transition-colors disabled:opacity-50">
                            <XCircle size={22} />
                        </button>
                    )}
                    {!isSupportChat && (
                        <button onClick={() => setIsReportModalOpen(true)} title="Reportar Problema" className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                            <Flag size={20} />
                        </button>
                    )}
                    {canFinalize && (<button onClick={() => setIsFinalizeModalOpen(true)} className="flex items-center bg-green-500 text-white font-semibold px-3 py-1.5 rounded-lg text-sm shadow-md active:scale-95 transition-transform"> <CheckCircle className="w-4 h-4 mr-1.5" /> Finalizar </button>)}
                </div>
            </header>

            <main ref={chatContainerRef} onScroll={handleScroll} className="flex-grow overflow-y-auto p-4 text-left">
                <div className="space-y-3 pb-10">
                    {isLoadingMore && <div className="text-center p-2 text-xs text-gray-500 italic">Carregando histórico anterior...</div>}
                    {messages.map(msg => {
                        if (msg.type === 'system') {
                            return (
                                <div key={msg.id} className="flex justify-center my-6 animate-fade-in">
                                    <div className="bg-slate-100 border border-slate-200 px-6 py-2.5 rounded-2xl max-w-[85%] text-center shadow-sm">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Aviso do Sistema</p>
                                        <p className="text-xs font-bold text-slate-600 leading-relaxed">{msg.text}</p>
                                    </div>
                                </div>
                            );
                        }

                        if (msg.type === 'quote') return <QuoteMessage key={msg.id} message={msg} user={user} onRespond={handleRespondToQuote} onEdit={handleOpenQuoteModal} chatInfo={chatInfo} />;
                        if (msg.type === 'image') return <div key={msg.id} className={`flex items-end ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'}`}><img src={msg.imageUrl} alt="Imagem" className="max-w-xs rounded-2xl shadow-md border-4 border-white" style={{maxWidth: '70%'}}/></div>;
                        if (msg.type === 'location') {
                            const url = `https://www.google.com/maps?q=${msg.location.latitude},${msg.location.longitude}`;
                            const staticMapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${msg.location.longitude-0.005},${msg.location.latitude-0.005},${msg.location.longitude+0.005},${msg.location.latitude+0.005}&layer=mapnik&marker=${msg.location.latitude},${msg.location.longitude}`;
                            return (
                                <div key={msg.id} className={`flex items-end ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'}`}>
                                    <a href={url} target="_blank" rel="noopener noreferrer" className={`rounded-2xl overflow-hidden max-w-xs md:max-w-md shadow-md ${msg.senderId === user.uid ? 'bg-blue-600 text-white' : 'bg-white text-gray-800'}`}>
                                        <div className="w-full h-32 bg-gray-300">
                                            <iframe width="100%" height="100%" frameBorder="0" scrolling="no" marginHeight="0" marginWidth="0" src={staticMapUrl} style={{pointerEvents: 'none'}}></iframe>
                                        </div>
                                        <div className="p-3">
                                            <p className="font-bold text-sm leading-tight">{msg.text}</p>
                                            <p className="text-[10px] mt-1 opacity-70 uppercase font-black tracking-widest">Clique para ver no mapa</p>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (msg.type === 'invoice') return <InvoiceMessage key={msg.id} message={msg} activeRole={activeRole} chatInfo={chatInfo} user={user} chatId={chatId} />;

                        const isFromSupport = msg.senderId === "SYSTEM_ADMIN_SUPPORT" || msg.senderId === "system_support";
                        return (
                            <div key={msg.id} className={`flex items-end ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'}`}>
                                <div className={`p-3 rounded-[1.2rem] max-w-xs md:max-w-md shadow-sm ${
                                    msg.senderId === user.uid
                                        ? 'bg-blue-600 text-white rounded-br-none'
                                        : isFromSupport
                                            ? 'bg-slate-800 text-blue-100 rounded-bl-none border border-blue-500/20'
                                            : 'bg-white text-gray-800 rounded-bl-none'
                                }`}>
                                    {isFromSupport && <p className="text-[8px] font-black uppercase text-blue-400 mb-1 tracking-widest">Suporte Oficial</p>}
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                                </div>
                            </div>
                        );
                    })}

                    {isChatClosed && leftoverProposals.length > 0 && (
                        <div className="mt-8 animate-slide-up">
                            <div className="bg-white p-5 rounded-[2rem] border-2 border-blue-50 shadow-xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5"><RefreshCw size={80} className="text-blue-600" /></div>
                                <div className="relative z-10">
                                    <div className="flex items-center text-blue-600 mb-2">
                                        <CalendarCheck size={20} className="mr-2" />
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Compromisso Futuro</span>
                                    </div>
                                    <h4 className="text-gray-800 font-black leading-tight mb-2">
                                        {activeRole === 'client' ? `Notamos orçamentos pendentes:` : `Existem serviços adiados:`}
                                    </h4>
                                    <div className="space-y-2 mb-4">
                                        {leftoverProposals.map(p => (
                                            <div key={p.id} className="bg-blue-50/50 p-3 rounded-xl flex justify-between items-center border border-blue-100/50">
                                                <span className="text-xs font-bold text-blue-800 truncate mr-2">{p.items?.[0]?.description || "Serviço Adiado"}</span>
                                                <span className="text-xs font-black text-blue-600">R$ {p.total.toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {activeRole === 'client' && (
                                        <button
                                            onClick={() => handleResumeService(leftoverProposals[0])}
                                            className="w-full py-3 bg-blue-600 text-white font-black text-xs rounded-xl shadow-lg shadow-blue-100 flex items-center justify-center uppercase tracking-widest active:scale-95 transition-all"
                                        >
                                            <RefreshCw size={14} className="mr-2" /> Retomar Agora
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </main>

            <footer className="p-2 bg-white border-t shrink-0 relative">
                {isQuickReplyOpen && quickReplies.length > 0 && (
                    <div className="absolute bottom-full left-2 mb-2 w-full max-w-sm animate-slide-up">
                        <div className="bg-white p-3 rounded-2xl shadow-2xl border border-blue-50 space-y-2">
                            <div className="flex items-center text-blue-600 mb-2 px-1">
                                <Zap size={14} className="mr-2 fill-current"/>
                                <span className="text-[10px] font-black uppercase tracking-widest">Respostas Rápidas</span>
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                                {quickReplies.map((reply, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSendMessage(reply)}
                                        className="w-full text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-xl transition-all border border-transparent hover:border-blue-100 active:scale-[0.98]"
                                    >
                                        <p className="text-sm font-medium text-gray-700">{reply}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {isAttachmentMenuOpen && (<div className="absolute bottom-full left-2 mb-2 w-full max-w-sm animate-slide-up"><div className="bg-white p-2 rounded-2xl shadow-2xl border grid grid-cols-4 gap-2"><button onClick={() => handleNavigateToCapture('photo')} className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-100"><Camera className="w-7 h-7 text-red-500 mb-1" /><span className="text-[10px] font-bold">Câmera</span></button><button onClick={() => handleNavigateToCapture('video')} className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-100"><Video className="w-7 h-7 text-purple-600 mb-1" /><span className="text-[10px] font-bold">Vídeo</span></button><button onClick={handleGalleryClick} className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-100"><ImageIcon className="w-7 h-7 text-green-600 mb-1" /><span className="text-[10px] font-bold">Galeria</span></button><button onClick={() => {setIsAttachmentMenuOpen(false); setIsLocationModalOpen(true);}} className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-100"><MapPin className="w-7 h-7 text-blue-500 mb-1" /><span className="text-[10px] font-bold">Localização</span></button></div></div>)}

                <div className="flex items-end">
                    {canSendAttachments && (<button onClick={() => setIsAttachmentMenuOpen(prev => !prev)} className="p-3 text-gray-400 hover:text-blue-600 transition-colors"><Plus className={`w-6 h-6 transition-transform duration-300 ${isAttachmentMenuOpen ? 'rotate-45' : ''}`} /></button>)}

                    {isServi && !isChatClosed && !isSupportChat && (
                        <button
                            onClick={() => setIsQuickReplyOpen(prev => !prev)}
                            className={`p-3 transition-all duration-300 ${isQuickReplyOpen ? 'text-blue-600' : 'text-gray-400 hover:text-blue-600'}`}
                        >
                            <Zap size={24} className={isQuickReplyOpen ? 'fill-current' : ''} />
                        </button>
                    )}

                    {canFinalize && (<button onClick={() => handleOpenQuoteModal()} className="p-3 text-gray-400 hover:text-blue-600 transition-colors"><PenSquare className="w-6 h-6" /></button>)}

                    <textarea
                        ref={textAreaRef}
                        rows="1"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isChatClosed ? "Serviço finalizado" : "Mensagem..."}
                        disabled={isChatClosed}
                        className="w-full mx-2 p-3 border-2 border-gray-100 rounded-2xl bg-gray-50 text-sm focus:border-blue-500 outline-none transition-all resize-none overflow-y-auto"
                        style={{ maxHeight: '120px' }}
                    />

                    <button
                        onClick={() => handleSendMessage()}
                        disabled={isChatClosed || !newMessage.trim()}
                        className="p-3 mb-0.5 bg-blue-600 text-white rounded-full shadow-lg shadow-blue-100 disabled:bg-gray-300 disabled:shadow-none transition-all active:scale-90"
                    >
                        <Send size={18}/>
                    </button>
                </div>
            </footer>

            {showEndConfirmation && (
                <ConfirmationModal
                    title="Encerrar Atendimento"
                    message={activeRole === 'servi' ? "Deseja realmente encerrar este atendimento por falta de acordo?" : "Deseja cancelar sua solicitação?"}
                    confirmText="Sim, Encerrar"
                    cancelText="Não, Voltar"
                    onConfirm={handleConfirmEndChat}
                    onClose={() => setShowEndConfirmation(false)}
                />
            )}

            {isFinalizeModalOpen && <FinalizeServiceModal chat={{ ...chatInfo, id: chatId }} onClose={() => setIsFinalizeModalOpen(false)} onSubmit={handleFinalizeSubmit} />}
            {isQuoteModalOpen && <QuoteModal onClose={handleCloseQuoteModal} onSubmitQuote={handleSubmitQuote} user={user} fullUserData={fullUserData} quoteToEdit={quoteToEdit} />}
            {isReportModalOpen && <ReportModal chatId={chatId} targetUserId={otherParticipantId} targetUserName={otherParticipant?.name} serviceTitle={chatInfo.serviceTitle} onClose={() => setIsReportModalOpen(false)} />}
            {isLocationModalOpen && (<LocationTypeModal onClose={() => setIsLocationModalOpen(false)} onCurrentLocation={handleGetCurrentLocation} onManualLocation={handleOpenManualLocation} />)}
            {isAddressModalOpen && (<AddressInputModal onClose={() => setIsAddressModalOpen(false)} onSendAddress={handleSendLocation} />)}
        </div>
    );
};

export default ChatScreen;
